/**
 * Feature E03-F02-T04 — Pre-Kickoff panel (анализ + выбор/правка драфта).
 *
 * The React shell for Stage-1 (Pre-Kickoff). 1:1 port of the legacy SPA render
 * (`../EER app/public/assets/js/project.js` ~853-1052 + `openEmailDialog`
 * ~1055): the company/position brief, the three option cards (A: Kick-off, B:
 * Request Details, C: Hybrid) with the AI-Recommendation badge, and the draft
 * editor pre-filled from the selected option.
 *
 * Thin shell — all non-render rules live in `./prekickoffModel.ts`
 * (buildPreKickoffBrief / buildOptionCards / draftToEditorState / isDraftSendable
 * / parsePersistedArtifacts) and `./prekickoffApi.ts` (useRunPreKickoffAnalysis).
 * The shapes come from `@eer/shared` so the panel speaks the exact contract the
 * Fastify route (`POST /projects/:id/ai/pre_kickoff_analysis`, T03) returns.
 *
 * Data flow:
 *   - on open it re-hydrates any PERSISTED artifacts (T02 UPSERT) handed in via
 *     `initialArtifacts` so a previously-run analysis renders WITHOUT re-running
 *     the AI (parsePersistedArtifacts → {analysis,drafts} | null);
 *   - "Run AI Analysis" fires the mutation; on a SUCCESS body it swaps in the
 *     freshly returned {analysis,drafts} (and the host refetches the project so
 *     the DB stays the source of truth — the api hook invalidates the per-project
 *     key); a `{success:false}` body is a graceful failure → toast the error (no
 *     throw); a transport failure → onError toast.
 *
 * Display invariants (carried from the model docs):
 *   - the AI-Recommendation badge follows `analysis.recommendation` (default C);
 *   - the draft `to` is the server-injected contact email (editable for typos but
 *     pre-filled from the persisted draft — never a forged value).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  analysisToEditState,
  applyPlanToUpdates,
  buildOptionCards,
  buildPreKickoffBrief,
  diffAnalysisEdit,
  draftToEditorState,
  formatLastAnalysis,
  isDraftSendable,
  parsePersistedArtifacts,
  planApplyToProject,
  type AnalysisEditState,
  type ApplyToProjectPlan,
  type DraftEditorState,
  type PersistedArtifactRow,
  type PreKickoffArtifacts,
  type PreKickoffOptionCard,
} from './prekickoffModel.js';
import {
  useRunPreKickoffAnalysis,
  useSaveAnalysis,
  isPreKickoffSuccess,
  readValidationFields,
} from './prekickoffApi.js';
import { readManualContext } from './prekickoffModel.js';
import { useAddInformationDraft } from './useAddInformationDraft.js';
import { PreKickoffDataPanel } from './PreKickoffDataPanel.js';
import { AddInformationPanel } from './AddInformationPanel.js';
import { isConfirmRequired, type PreKickoffDraftOption } from '@eer/shared';
import { useToast } from '../ui/ToastContext.js';
import { RichTextEditor } from '../ui/RichTextEditor.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { useProjectDetail, useUpdateProject } from '../project/projectDetailApi.js';
import { useProjectContacts } from '../contacts/directoryApi.js';
import { errorMessage, errorBodyOf } from '../api/client.js';
import { getCachedUser } from '../api/session.js';

/**
 * PK-QA-1 — human labels for the hard-missing invariant fields the pre-kickoff
 * 400 `VALIDATION_ERROR` reports (`client_name` / `role_name` / `contact_email`).
 * These have NO confirm path — the user must fill them in first.
 */
const MISSING_FIELD_LABELS: Record<string, string> = {
  client_name: 'Company',
  role_name: 'Position',
  contact_email: 'Contact email',
};

/** One editable brief field's metadata (key into the edit state + label + widget). */
interface BriefEditFieldMeta {
  readonly key: Exclude<keyof AnalysisEditState, 'key_points'>;
  readonly label: string;
  readonly multiline: boolean;
}

/** Company card editable fields (Name is a project scalar → not here — PK4 ADR). */
const COMPANY_EDIT_FIELDS: readonly BriefEditFieldMeta[] = [
  { key: 'industry', label: 'Industry', multiline: false },
  { key: 'company_type', label: 'Type', multiline: false },
  { key: 'offices', label: 'Offices', multiline: false },
  { key: 'products', label: 'Products', multiline: true },
];

/** Position card editable fields (Title is a project scalar → not here — PK4 ADR). */
const POSITION_EDIT_FIELDS: readonly BriefEditFieldMeta[] = [
  { key: 'country', label: 'Country', multiline: false },
  { key: 'scope', label: 'Scope', multiline: true },
  { key: 'key_requirements', label: 'Requirements', multiline: true },
  { key: 'role_goal', label: 'Role/Goal', multiline: true },
];

export interface PreKickoffPanelProps {
  /** Project id — POST target /projects/:id/ai/pre_kickoff_analysis. */
  readonly projectId: number;
  /**
   * Persisted Pre-Kickoff artifacts from the project detail card (T02 UPSERT) —
   * re-hydrated on open so a prior analysis renders without re-running the AI.
   */
  readonly initialArtifacts?: readonly PersistedArtifactRow[] | null;
  /** Project header fields — take precedence over the model's names in the brief. */
  readonly project?: { readonly clientName?: string | null; readonly roleName?: string | null };
  /**
   * Optional "send this draft" handoff (Stage-1 send flow, E05-F06). When omitted
   * the editor still renders + validates, but the Send button is hidden (the panel
   * is then read/edit only).
   */
  readonly onSend?: (option: PreKickoffDraftOption, draft: DraftEditorState) => void;
  /**
   * PK-QA-1 UX — report the header form-field keys the analysis 400
   * `VALIDATION_ERROR` flagged as missing (`client_name` / `role_name` /
   * `contact_email`), so the shared parent can highlight the actual fields on the
   * project header (a toast alone does not point at WHERE they are). Called with
   * the missing keys on a validation failure and with `[]` on a successful run
   * (clear the highlight on a successful retry).
   */
  readonly onValidationMissing?: (fields: readonly string[]) => void;
}

/** The fixed A/B/C ordering used for option cards and editor selection. */
type SelectedOption = PreKickoffDraftOption;

export function PreKickoffPanel({
  projectId,
  initialArtifacts,
  project,
  onSend,
  onValidationMissing,
}: PreKickoffPanelProps): React.JSX.Element {
  const toast = useToast();
  const mutation = useRunPreKickoffAnalysis(projectId);
  const saveMutation = useSaveAnalysis(projectId);

  // The manual context lives on projects.data.manual_context.text. It is not a prop
  // here, so read it from the SHARED per-project detail cache (qk.projects.detail) —
  // the host (ProjectDetailView) already fetched it, so this is a cache hit, not a
  // new request. The "Pre-Kickoff Data" panel renders the AI analysis + this context.
  const detailQuery = useProjectDetail(projectId);
  const manualContext = readManualContext(detailQuery.data?.project.data ?? null);

  // PK-QA-6 — the shared Add-Information draft (context text + pending files)
  // lives HERE (parent) so `runAnalysis` can save-first when it is dirty; the
  // AddInformationPanel below is a controlled view over the same draft. The
  // dirty-compare baseline is the persisted manual-context text.
  const addDraft = useAddInformationDraft({ projectId, savedManualText: manualContext });

  // PK-QA-2 — "Apply to Project": copy the analysis company/position into the
  // project scalars via the existing updateProject mutation (auto-sync stays
  // forbidden — PK4 ADR), with a per-field confirm on a real overwrite conflict.
  const updateMutation = useUpdateProject();
  const { confirm, confirmDialog } = useConfirm();

  // The rendered analysis + drafts: seeded from the persisted artifacts, replaced
  // by a fresh run's response. `null` until the analysis has ever been run.
  const [data, setData] = useState<PreKickoffArtifacts | null>(() =>
    parsePersistedArtifacts(initialArtifacts),
  );

  // E03-F02-T06 — "last analyzed: <when> · <who>" attribution near the button.
  // The mutation invalidates `qk.projects.detail(projectId)` on success, so a
  // real run eventually re-hydrates `last_analysis_at`/`last_analysis_by` from
  // the re-fetched project (source of truth). This local override just covers
  // the gap between "the run just finished" and "the refetch landed" — set from
  // THIS session's own run, never persisted, always superseded once the server
  // field shows a value.
  const [localLastRun, setLocalLastRun] = useState<{
    readonly at: string;
    readonly who: string | null;
  } | null>(null);
  const lastAnalysisAt = detailQuery.data?.project.last_analysis_at ?? localLastRun?.at ?? null;
  const lastAnalysisBy = detailQuery.data?.project.last_analysis_by ?? localLastRun?.who ?? null;
  const lastAnalysisLabel = formatLastAnalysis(lastAnalysisAt, lastAnalysisBy);

  // Re-hydrate if the host swaps in a different project's artifacts (refetch).
  // Also drop any open PK4 edit — a fresh artifact set (project switch, or the
  // post-Save invalidation) must not leave a stale editor over new data.
  useEffect(() => {
    setData(parsePersistedArtifacts(initialArtifacts));
    setEditState(null);
  }, [initialArtifacts]);

  // Which option's draft is open in the editor. Defaults to the recommendation
  // once an analysis is present (the badge-carrying card).
  const [selected, setSelected] = useState<SelectedOption | null>(null);
  const [editor, setEditor] = useState<DraftEditorState | null>(null);

  // PK4 (E03-F02-T08) — inline edit of the persisted analysis brief. `editState`
  // is non-null exactly while edit mode is open (seeded from the loaded analysis
  // via `analysisToEditState`; free-form until Save diffs it back to a patch).
  const [editState, setEditState] = useState<AnalysisEditState | null>(null);
  const editing = editState !== null;

  const brief = useMemo(
    () => (data ? buildPreKickoffBrief(data.analysis, project) : null),
    [data, project],
  );
  // Identity fields (project scalars) shown read-only in the PK4 edit view — reuse
  // the brief's already-computed values so the header-precedence rule is not
  // duplicated here (they are edited via the project header, not this route).
  const companyName = brief?.company.find((f) => f.label === 'Name')?.value ?? '';
  const positionTitle = brief?.position.find((f) => f.label === 'Title')?.value ?? '';
  const cards = useMemo<readonly PreKickoffOptionCard[]>(
    () => (data ? buildOptionCards(data.analysis, data.drafts) : []),
    [data],
  );

  // PK-QA-2 — the current project SCALARS (client_name / role_name) as actually
  // persisted (NOT the brief's analysis-fallback display value) — the values the
  // apply-plan compares the AI suggestion against for its silent/confirm decision.
  const currentClientName = detailQuery.data?.project.client_name ?? '';
  const currentRoleName = detailQuery.data?.project.role_name ?? '';
  // PK-QA-16 — the human-entered company website (`Client.website`). It is a Client
  // scalar (like client_name), NOT an analysis field, so it is edited via the
  // `client_website` update-bag key (useUpdateProject) and NEVER the analysis-diff
  // save path (COMPANY_EDIT_FIELDS → diffAnalysisEdit → saveMutation). See the
  // dedicated inline editor rendered in the Company card below.
  const currentClientWebsite = detailQuery.data?.project.client_website ?? null;
  const applyPlan = useMemo<ApplyToProjectPlan | null>(
    () =>
      data
        ? planApplyToProject(data.analysis, {
            clientName: currentClientName,
            roleName: currentRoleName,
          })
        : null,
    [data, currentClientName, currentRoleName],
  );

  // When the analysis (re)loads and nothing is selected yet, open the recommended
  // option's draft in the editor (default C — the recommendation schema default).
  useEffect(() => {
    if (!data) {
      setSelected(null);
      setEditor(null);
      return;
    }
    if (selected === null) {
      const recommended = cards.find((c) => c.recommended) ?? cards[0];
      if (recommended) {
        setSelected(recommended.option);
        setEditor(draftToEditorState(recommended.draft));
      }
    }
  }, [data, cards, selected]);

  // KS2-08 — кто получит письмо. Источник — карточка адресатов проекта, тот же,
  // что теперь читает отправка на сервере. Поле "To" в драфте — снимок, сделанный
  // моделью в момент генерации; показывать его как адрес отправки значит показывать
  // не тот адрес, по которому письмо уйдёт. Ксения: «письмо ушло не назначенному
  // новому основному контакту, а первому».
  const contactsQuery = useProjectContacts(projectId);
  const cardPrimary = contactsQuery.data?.primary ?? null;
  const cardTo = (cardPrimary?.email ?? '').trim();
  const cardCc = (contactsQuery.data?.cc ?? []).filter((c) => (c.email ?? '').trim().length > 0);
  // Что реально уйдёт: адрес из карточки, если он есть, иначе снимок (проекты
  // старше Этапа 5 карточки не имеют — сервер там тоже берёт снимок).
  const outgoing = editor ? (cardTo ? { ...editor, to: cardTo } : editor) : null;

  function openOption(option: SelectedOption): void {
    const card = cards.find((c) => c.option === option);
    if (!card) return;
    setSelected(option);
    setEditor(draftToEditorState(card.draft));
  }

  /**
   * PK-QA-6 — combined "Save & Run Analysis". When the Add-Information panel has
   * unsaved context text or pending files, this FIRST persists them (the same
   * multipart PATCH the "Save Information" button uses) and only THEN runs the
   * analysis — so the LLM sees the freshly-saved material, not a stale/empty
   * `projects.data`. When clean, it runs the analysis directly as before. Both
   * steps are awaited and it is ONE explicit user action (no silent auto-save) —
   * the button copy + a save-success toast make the save visible.
   */
  async function runAnalysis(): Promise<void> {
    if (mutation.isPending || addDraft.saving) return;

    // Save-first when dirty. A failed save aborts the run (never analyze stale
    // context) and the analysis never fires — the user re-tries explicitly.
    if (addDraft.dirty) {
      try {
        await addDraft.save();
        toast.success('Changes saved successfully');
      } catch {
        toast.error('Save error');
        return;
      }
    }

    await executeAnalysis(false);
  }

  /**
   * Issue the analysis request (POST .../pre_kickoff_analysis) and branch on the
   * outcome. PK-QA-1:
   *   - a soft 412 `CONFIRM_REQUIRED(nothing_to_analyze)` → confirm dialog → on
   *     "Run anyway" re-issue the SAME request with `confirmed:true`;
   *   - a hard 400 `VALIDATION_ERROR` (missing client/role/contact) → surface the
   *     missing fields, NO confirm dialog (non-overridable server invariant);
   *   - anything else (transport/parse/graceful `{success:false}`) → generic toast.
   * `confirmed` guards the recursion so a second 412 never loops.
   */
  async function executeAnalysis(confirmed: boolean): Promise<void> {
    try {
      const res = await mutation.mutateAsync({ confirmed });
      if (isPreKickoffSuccess(res)) {
        setData({ analysis: res.analysis, drafts: res.drafts });
        // A successful run means the required fields were present → clear any
        // header highlight left over from an earlier VALIDATION_ERROR.
        onValidationMissing?.([]);
        // Force re-selection of the (possibly new) recommendation.
        setSelected(null);
        setEditor(null);
        // Optimistic "last analyzed" attribution — the current session's own
        // run, shown immediately (see `localLastRun` above; superseded once
        // the invalidated project-detail refetch lands with the server value).
        setLocalLastRun({
          at: new Date().toISOString(),
          who: getCachedUser()?.name ?? null,
        });
        toast.success('Pre-Kickoff analysis complete');
      } else {
        toast.error(res.error || 'Analysis failed');
      }
    } catch (error) {
      const body = errorBodyOf(error);
      if (!confirmed && isConfirmRequired(body) === 'nothing_to_analyze') {
        const ok = await confirm({
          title: 'Run analysis?',
          message: 'There is no material to analyse yet. Run the analysis anyway?',
          confirmLabel: 'Run anyway',
          cancelLabel: 'Cancel',
          testId: 'prekickoff-confirm-analyze',
        });
        if (ok) await executeAnalysis(true);
        return;
      }
      const missing = readValidationFields(body);
      if (missing && missing.length > 0) {
        const labels = missing.map((f) => MISSING_FIELD_LABELS[f] ?? f).join(', ');
        toast.error(`Add the required details first: ${labels}`);
        // PK-QA-1 UX — also point at WHERE the fields are: hand the missing keys to
        // the parent so the project header outlines the actual inputs (the toast
        // names them, the header shows them).
        onValidationMissing?.(missing);
        return;
      }
      toast.error('Error running pre-kickoff analysis');
    }
  }

  /**
   * PK-QA-2 — "Apply to Project": copy the analysis `company_name` / `position_title`
   * into the project scalars (`client_name` / `role_name`) via the existing
   * updateProject mutation (one PATCH; `forceNewClient` NOT passed — normal
   * link/rename logic). Per field: an empty or already-equal scalar is written
   * silently; a non-empty scalar that DIFFERS is a real overwrite and is gated
   * behind a pure client-side confirm listing "old → new" (NOT the PK-QA-1 server
   * confirm). On success the updateProject cache-invalidation refreshes the header.
   */
  async function applyToProject(): Promise<void> {
    const plan = applyPlan;
    if (!plan || !plan.canApply || updateMutation.isPending) return;

    if (plan.conflicts.length > 0) {
      const ok = await confirm({
        title: 'Overwrite project fields?',
        confirmLabel: 'Apply',
        cancelLabel: 'Cancel',
        testId: 'prekickoff-apply-confirm',
        message: (
          <div data-testid="prekickoff-apply-confirm-fields">
            <p className="text-sm text-text">
              These project fields already have a value that differs from the AI suggestion.
              Overwrite them?
            </p>
            <ul className="mt-2 space-y-1">
              {plan.conflicts.map((f) => (
                <li key={f.key} className="text-sm">
                  <span className="font-medium text-text-strong">{f.label}:</span>{' '}
                  <span className="text-text-muted line-through">{f.current}</span>
                  {' → '}
                  <span className="font-semibold text-text-strong">{f.suggestion}</span>
                </li>
              ))}
            </ul>
          </div>
        ),
      });
      if (!ok) return;
    }

    updateMutation.mutate(
      { projectId, updates: applyPlanToUpdates(plan) },
      {
        onSuccess: () => toast.success('Applied to project'),
        onError: () => toast.error('Could not apply to project'),
      },
    );
  }

  // --- PK4 edit-mode handlers ------------------------------------------------

  /** Enter edit mode, seeding the editors from the loaded analysis. */
  function startEdit(): void {
    if (!data) return;
    setEditState(analysisToEditState(data.analysis));
  }

  /** Discard local edits and leave edit mode (Cancel). */
  function cancelEdit(): void {
    setEditState(null);
  }

  /** Patch one string brief field in the edit state. */
  function setField(key: BriefEditFieldMeta['key'], value: string): void {
    setEditState((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  /** key_points row ops (edit one row / add a blank row / remove a row). */
  function setKeyPoint(index: number, value: string): void {
    setEditState((prev) =>
      prev
        ? { ...prev, key_points: prev.key_points.map((p, i) => (i === index ? value : p)) }
        : prev,
    );
  }
  function addKeyPoint(): void {
    setEditState((prev) => (prev ? { ...prev, key_points: [...prev.key_points, ''] } : prev));
  }
  function removeKeyPoint(index: number): void {
    setEditState((prev) =>
      prev ? { ...prev, key_points: prev.key_points.filter((_, i) => i !== index) } : prev,
    );
  }

  /**
   * Save (PK4): diff the edit state against the loaded analysis and PATCH only the
   * changed fields. An empty diff means nothing changed → just close edit mode (the
   * route would 400 on an empty patch). On success the mutation invalidates the
   * project-detail key, which re-hydrates `data` and (via the `initialArtifacts`
   * effect) clears `editState` — so the brief re-renders read-only with saved values.
   */
  function saveEdit(): void {
    if (!data || !editState || saveMutation.isPending) return;
    const patch = diffAnalysisEdit(data.analysis, editState);
    if (!patch) {
      cancelEdit();
      return;
    }
    saveMutation.mutate(patch, {
      onSuccess: () => {
        toast.success('Analysis saved');
        setEditState(null);
      },
      onError: (error) => {
        toast.error(errorMessage(error, 'Failed to save analysis'));
      },
    });
  }

  const analyzing = mutation.isPending;
  const savingInfo = addDraft.saving; // PK-QA-6 save-first step in flight
  const busy = analyzing || savingInfo;
  const saving = saveMutation.isPending;

  return (
    <div data-testid="prekickoff-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="brand-font text-xl font-bold text-text-strong">Pre-Kickoff Analysis</h2>
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            data-testid="prekickoff-run"
            onClick={() => void runAnalysis()}
            disabled={busy}
            className="rounded bg-primary px-4 py-2 text-sm font-semibold text-surface hover:bg-primary-hover disabled:opacity-60"
          >
            {busy ? (
              <>
                <i
                  className="fa-solid fa-spinner fa-spin mr-2"
                  data-testid="prekickoff-run-spinner"
                  aria-hidden
                />
                {/* PK-QA-6: surface the save-first step so the user can tell the
                    combined action saved before it analyzes. */}
                {savingInfo ? 'Saving…' : 'Analyzing…'}
              </>
            ) : (
              <>
                <i className="fa-solid fa-wand-magic-sparkles mr-2" aria-hidden />
                {/* E03-F02-T09: drive the label off the REAL "has ever run"
                    signal (`lastAnalysisAt`, only set by an ai_analysis_completed
                    event) — NOT `data` (which could be a stale artifact). Never
                    run → "AI Analysis"; already run → "Re-Run Analysis". PK-QA-6:
                    when the Add-Information panel is dirty the click is a combined
                    save-then-analyze → reflect that in the copy. */}
                {addDraft.dirty
                  ? 'Save & Run Analysis'
                  : lastAnalysisAt == null
                    ? 'AI Analysis'
                    : 'Re-Run Analysis'}
              </>
            )}
          </button>
          {/* E03-F02-T06 — "last analyzed: <when> · <who>" once an analysis has
              ever completed (persisted or this session's own fresh run). */}
          {lastAnalysisLabel ? (
            <span data-testid="prekickoff-last-run" className="text-xs text-text-muted">
              {lastAnalysisLabel}
            </span>
          ) : null}
        </div>
      </div>

      {/* E02-F07-T07 — two-column parity with v1 (legacy `flex gap-6`, left
          `flex-1` / right `w-1/3`; PM asked for 40/60 at visual acceptance
          2026-07-08): the AI-analysis area on the LEFT (60%), the "Pre-Kickoff
          Data" + "Add Information" sidebar on the RIGHT (40%). Collapses to a
          single stacked column below `lg` so the responsive layout stays intact. */}
      <div className="grid gap-6 lg:grid-cols-5 lg:items-start">
        {/* LEFT (60%) — analysis brief + option cards + draft editor */}
        <div data-testid="prekickoff-analysis-col" className="space-y-6 lg:col-span-3">
          {data === null ? (
            // E03-F02-T06 — before the FIRST analysis run, the data block reads as
            // blurred/overlaid (frosted-glass placeholder) rather than a plain empty
            // box, with "Run AI analysis first" as the overlay's own message.
            <div
              data-testid="prekickoff-empty"
              className="relative overflow-hidden rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-text-muted"
            >
              <div
                aria-hidden
                data-testid="prekickoff-empty-blur-overlay"
                className="pointer-events-none absolute inset-0 backdrop-blur-sm"
              />
              <div className="relative flex flex-col items-center gap-2">
                <i
                  className="fa-solid fa-wand-magic-sparkles text-lg text-text-muted"
                  aria-hidden
                />
                <p className="font-semibold text-text-strong">Run AI analysis first</p>
                <p className="text-xs text-text-muted">
                  Run the AI analysis to summarise the inquiry and draft three reply options.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Brief: inquiry summary + company / position cards + key points.
              PK4 (E03-F02-T08): an Edit toggle flips the read-only rows into
              inline editors + Save/Cancel. Only reachable here (data !== null) —
              never over the blur/empty state, so the PK1-3 "not analyzed" gate
              (E03-F02-T09) is untouched. */}
              {brief ? (
                <section data-testid="prekickoff-brief" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-text-strong">Analysis brief</h3>
                    {editing ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          data-testid="prekickoff-cancel"
                          onClick={cancelEdit}
                          disabled={saving}
                          className="rounded border border-border px-3 py-1.5 text-sm font-medium text-text-muted hover:bg-surface-muted disabled:opacity-60"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          data-testid="prekickoff-save"
                          onClick={saveEdit}
                          disabled={saving}
                          className="rounded bg-primary px-3 py-1.5 text-sm font-semibold text-surface hover:bg-primary-hover disabled:opacity-60"
                        >
                          {saving ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
                              Saving…
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-floppy-disk mr-2" aria-hidden />
                              Save
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {/* PK-QA-2 — Apply to Project: copy the analysed company /
                            position into the project scalars. Disabled (shown as an
                            "Applied" marker) when the project already equals both
                            non-empty suggestions; hidden when there is nothing to
                            apply (both suggestions empty). */}
                        {applyPlan && applyPlan.alreadyApplied ? (
                          <span
                            data-testid="prekickoff-apply-applied"
                            className="inline-flex items-center gap-1 text-xs font-medium text-text-muted"
                            title="Company and position already match the analysis"
                          >
                            <i className="fa-solid fa-check text-green-600" aria-hidden />
                            Applied
                          </span>
                        ) : applyPlan && applyPlan.canApply ? (
                          <button
                            type="button"
                            data-testid="prekickoff-apply"
                            onClick={() => void applyToProject()}
                            disabled={updateMutation.isPending}
                            title="Copy the analysed company & position into the project"
                            className="rounded border border-border px-3 py-1.5 text-sm font-medium text-text-strong hover:bg-surface-muted disabled:opacity-60"
                          >
                            <i
                              className="fa-solid fa-arrow-up-right-from-square mr-2"
                              aria-hidden
                            />
                            {updateMutation.isPending ? 'Applying…' : 'Apply to project'}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          data-testid="prekickoff-edit-toggle"
                          onClick={startEdit}
                          className="rounded border border-border px-3 py-1.5 text-sm font-medium text-text-strong hover:bg-surface-muted"
                        >
                          <i className="fa-solid fa-pen mr-2" aria-hidden />
                          Edit
                        </button>
                      </div>
                    )}
                  </div>

                  {editing && editState ? (
                    <>
                      {/* Summary — editable textarea */}
                      <label className="block text-xs font-medium text-text-muted">
                        Summary
                        <textarea
                          data-testid="prekickoff-field-summary"
                          value={editState.summary}
                          rows={3}
                          onChange={(e) => setField('summary', e.target.value)}
                          className="mt-1 w-full resize-y rounded border border-border px-3 py-2 text-sm text-text focus:border-primary focus:outline-none"
                        />
                      </label>

                      <div className="grid gap-4 md:grid-cols-2">
                        <EditableBriefCard
                          testId="prekickoff-company"
                          title="Company"
                          identity={{ label: 'Name', value: companyName }}
                          fields={COMPANY_EDIT_FIELDS}
                          state={editState}
                          onChange={setField}
                          footer={
                            <CompanyWebsiteField
                              projectId={projectId}
                              website={currentClientWebsite}
                            />
                          }
                        />
                        <EditableBriefCard
                          testId="prekickoff-position"
                          title="Position"
                          identity={{ label: 'Title', value: positionTitle }}
                          fields={POSITION_EDIT_FIELDS}
                          state={editState}
                          onChange={setField}
                        />
                      </div>

                      {/* Key points — editable list (add / edit / remove rows) */}
                      <div
                        data-testid="prekickoff-key-points"
                        className="rounded border border-border p-4"
                      >
                        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                          Key points
                        </h4>
                        <div className="space-y-2">
                          {editState.key_points.map((point, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <input
                                type="text"
                                data-testid={`prekickoff-keypoint-${i}`}
                                value={point}
                                onChange={(e) => setKeyPoint(i, e.target.value)}
                                className="w-full rounded border border-border px-3 py-1.5 text-sm text-text focus:border-primary focus:outline-none"
                              />
                              <button
                                type="button"
                                data-testid={`prekickoff-keypoint-remove-${i}`}
                                onClick={() => removeKeyPoint(i)}
                                aria-label={`Remove key point ${i + 1}`}
                                className="shrink-0 rounded border border-border px-2 py-1.5 text-sm text-text-muted hover:bg-surface-muted"
                              >
                                <i className="fa-solid fa-trash" aria-hidden />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            data-testid="prekickoff-keypoint-add"
                            onClick={addKeyPoint}
                            className="rounded border border-dashed border-border px-3 py-1.5 text-sm font-medium text-text-muted hover:bg-surface-muted"
                          >
                            <i className="fa-solid fa-plus mr-2" aria-hidden />
                            Add key point
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {brief.summary ? (
                        <p
                          data-testid="prekickoff-summary"
                          className="rounded border border-border bg-blue-50/40 p-4 text-sm text-slate-700"
                        >
                          {brief.summary}
                        </p>
                      ) : null}

                      <div className="grid gap-4 md:grid-cols-2">
                        <BriefCard
                          testId="prekickoff-company"
                          title="Company"
                          fields={brief.company}
                          footer={
                            <CompanyWebsiteField
                              projectId={projectId}
                              website={currentClientWebsite}
                            />
                          }
                        />
                        <BriefCard
                          testId="prekickoff-position"
                          title="Position"
                          fields={brief.position}
                        />
                      </div>

                      {brief.keyPoints.length > 0 ? (
                        <div
                          data-testid="prekickoff-key-points"
                          className="rounded border border-border p-4"
                        >
                          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                            Key points
                          </h4>
                          <ul className="list-inside list-disc space-y-1 text-sm text-slate-700">
                            {brief.keyPoints.map((point, i) => (
                              <li key={i} data-testid="prekickoff-key-point">
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </>
                  )}
                </section>
              ) : null}

              {/* The three reply options (A/B/C) — recommended one carries the badge */}
              <section data-testid="prekickoff-options" className="grid gap-4 md:grid-cols-3">
                {cards.map((card) => (
                  <button
                    key={card.option}
                    type="button"
                    data-testid={`prekickoff-option-${card.option}`}
                    data-recommended={card.recommended ? 'true' : 'false'}
                    aria-pressed={selected === card.option}
                    onClick={() => openOption(card.option)}
                    className={`flex flex-col rounded-lg border p-4 text-left transition hover:border-primary ${
                      selected === card.option
                        ? 'border-primary ring-1 ring-primary'
                        : 'border-border'
                    }`}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-text-strong">{card.title}</span>
                      {card.recommended ? (
                        <span
                          data-testid={`prekickoff-badge-${card.option}`}
                          className="rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-primary"
                        >
                          AI Recommended
                        </span>
                      ) : null}
                    </div>
                    <span className="text-xs text-text-muted">{card.description}</span>
                  </button>
                ))}
              </section>

              {/* Draft editor — pre-filled from the selected option */}
              {selected && editor ? (
                <section
                  data-testid="prekickoff-editor"
                  className="space-y-3 rounded-lg border border-border p-4"
                >
                  <h3 className="text-sm font-semibold text-text-strong">Edit draft {selected}</h3>
                  {/* KS2-08 — адресат показывается тот, по которому письмо уйдёт.
                      Пока он назначен в карточке контактов, поле не редактируется:
                      правка здесь ничего бы не изменила (сервер читает карточку), а
                      выглядела бы как изменение получателя. Проекты без карточки
                      сохраняют прежнее поле ввода — там снимок и есть адрес. */}
                  {cardTo ? (
                    <div className="text-xs font-medium text-text-muted">
                      To
                      <div
                        data-testid="prekickoff-editor-to"
                        className="mt-1 flex flex-wrap items-center gap-2 rounded border border-border bg-surface-muted px-3 py-1.5 text-sm text-text"
                      >
                        <span className="font-medium text-text-strong">
                          {cardPrimary?.name?.trim() || 'Primary contact'}
                        </span>
                        <span className="text-text-muted">{cardTo}</span>
                      </div>
                      {cardCc.length > 0 ? (
                        <div className="mt-2">
                          Cc
                          <div
                            data-testid="prekickoff-editor-cc"
                            className="mt-1 flex flex-wrap gap-1.5"
                          >
                            {cardCc.map((c) => (
                              <span
                                key={c.id}
                                className="rounded-full border border-border px-2 py-0.5 text-xs text-text"
                              >
                                {c.name.trim() || 'Unnamed'} · {(c.email ?? '').trim()}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <p className="mt-1 text-[11px] text-text-muted">
                        Recipients come from the project Contacts card.
                      </p>
                    </div>
                  ) : (
                    <label className="block text-xs font-medium text-text-muted">
                      To
                      <input
                        type="email"
                        data-testid="prekickoff-editor-to"
                        value={editor.to}
                        onChange={(e) => setEditor({ ...editor, to: e.target.value })}
                        className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-text focus:border-primary focus:outline-none"
                      />
                    </label>
                  )}
                  <label className="block text-xs font-medium text-text-muted">
                    Subject
                    <input
                      type="text"
                      data-testid="prekickoff-editor-subject"
                      value={editor.subject}
                      onChange={(e) => setEditor({ ...editor, subject: e.target.value })}
                      className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-text focus:border-primary focus:outline-none"
                    />
                  </label>
                  <label className="block text-xs font-medium text-text-muted">
                    Body
                    {/* Этап 4 / KS-07: это тело письма клиенту — HTML. Поля
                        анализа выше остаются обычными textarea: они структурные
                        короткие ответы, а не форматированный текст наружу. */}
                    <RichTextEditor
                      testId="prekickoff-editor-body"
                      ariaLabel="Email body"
                      minHeightClass="min-h-[220px]"
                      value={editor.body}
                      onChange={(html) => setEditor({ ...editor, body: html })}
                    />
                  </label>
                  {onSend && outgoing ? (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        data-testid="prekickoff-editor-send"
                        disabled={!isDraftSendable(outgoing)}
                        // Отправляется `outgoing`, а не `editor`: сохранённый драфт
                        // должен нести тот же адрес, что уйдёт в письме, иначе
                        // журнал проекта снова разойдётся с действительностью.
                        onClick={() => onSend(selected, outgoing)}
                        className="rounded bg-accent px-4 py-2 text-sm font-semibold text-primary hover:bg-accent-hover disabled:opacity-60"
                      >
                        <i className="fa-solid fa-paper-plane mr-2" aria-hidden />
                        Send draft {selected}
                      </button>
                    </div>
                  ) : null}
                </section>
              ) : null}
            </>
          )}
        </div>

        {/* RIGHT (40%) — "Pre-Kickoff Data" AI-enrichment previews + the always-
            visible "Add Information" block (context / files / links / Position
            Type / Save Information). Legacy Stage-1 right column (E02-F04-T08). */}
        <aside data-testid="prekickoff-side-col" className="space-y-6 lg:col-span-2">
          {/* Hidden when neither an analysis nor a manual context is present. */}
          <PreKickoffDataPanel analysis={data?.analysis ?? null} manualContext={manualContext} />
          <AddInformationPanel
            key={projectId}
            projectId={projectId}
            data={detailQuery.data?.project.data ?? null}
            positionTypeId={detailQuery.data?.project.position_type_id ?? null}
            sourceEmailId={detailQuery.data?.project.source_email_id ?? null}
            draft={addDraft}
          />
        </aside>
      </div>

      {/* PK-QA-2 — styled overwrite-confirm for the "Apply to Project" conflict
          case (pure client-side confirm; not the PK-QA-1 server confirm). */}
      {confirmDialog}
    </div>
  );
}

/**
 * One brief card (Company or Position) — label/value rows, read-only. An optional
 * `footer` renders below the rows: the Company card uses it for the PK-QA-16
 * website editor (a Client scalar, independent of the analysis brief fields).
 */
function BriefCard({
  testId,
  title,
  fields,
  footer,
}: {
  readonly testId: string;
  readonly title: string;
  readonly fields: readonly { readonly label: string; readonly value: string }[];
  readonly footer?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div data-testid={testId} className="rounded border border-border p-4">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
        {title}
      </h4>
      {fields.length === 0 ? (
        <p className="text-sm text-text-muted">No data extracted.</p>
      ) : (
        <dl className="space-y-1">
          {fields.map((f) => (
            <div key={f.label} className="flex gap-2 text-sm">
              <dt className="w-28 shrink-0 font-medium text-text-muted">{f.label}</dt>
              <dd className="text-slate-700">{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {footer}
    </div>
  );
}

/**
 * PK4 editable variant of a brief card (Company or Position). Renders the identity
 * row (Name / Title) READ-ONLY — it is a project scalar edited via the header, not
 * this route (PK4 ADR) — followed by an input/textarea per editable field. Each
 * editor carries `data-testid="prekickoff-field-<key>"` (key = the analysis field
 * name) so the e2e can target a specific field.
 */
function EditableBriefCard({
  testId,
  title,
  identity,
  fields,
  state,
  onChange,
  footer,
}: {
  readonly testId: string;
  readonly title: string;
  readonly identity: { readonly label: string; readonly value: string };
  readonly fields: readonly BriefEditFieldMeta[];
  readonly state: AnalysisEditState;
  readonly onChange: (key: BriefEditFieldMeta['key'], value: string) => void;
  readonly footer?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div data-testid={testId} className="rounded border border-border p-4">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
        {title}
      </h4>
      <div className="space-y-2">
        {identity.value ? (
          <div className="flex gap-2 text-sm">
            <span className="w-28 shrink-0 font-medium text-text-muted">{identity.label}</span>
            <span className="text-slate-500">{identity.value}</span>
          </div>
        ) : null}
        {fields.map((f) => (
          <label key={f.key} className="block text-xs font-medium text-text-muted">
            {f.label}
            {f.multiline ? (
              <textarea
                data-testid={`prekickoff-field-${f.key}`}
                value={state[f.key]}
                rows={2}
                onChange={(e) => onChange(f.key, e.target.value)}
                className="mt-1 w-full resize-y rounded border border-border px-3 py-2 text-sm text-text focus:border-primary focus:outline-none"
              />
            ) : (
              <input
                type="text"
                data-testid={`prekickoff-field-${f.key}`}
                value={state[f.key]}
                onChange={(e) => onChange(f.key, e.target.value)}
                className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-text focus:border-primary focus:outline-none"
              />
            )}
          </label>
        ))}
        {footer}
      </div>
    </div>
  );
}

/**
 * PK-QA-16 — the "Website" (`Client.website`) inline editor. Website is a Client
 * scalar, NOT an analysis-brief field: it is persisted via the `client_website`
 * update-bag key (`useUpdateProject`) — the SAME path as "Apply to Project" and the
 * Company Name header edit — and NEVER through the analysis-diff save
 * (`diffAnalysisEdit`/`saveMutation`), which writes the `projects.data` blob. This
 * editor is therefore fully independent of the analysis "Edit" toggle: it manages
 * its own draft, seeded from the persisted value and re-seeded when that changes
 * (project switch / post-save invalidation). Save is enabled only when the trimmed
 * value differs from what is persisted; an empty value clears the field (server
 * normalizes "" → null). Its own `useUpdateProject` instance keeps its pending
 * state from coupling to the panel's "Apply to Project" button.
 */
function CompanyWebsiteField({
  projectId,
  website,
}: {
  readonly projectId: number;
  readonly website: string | null;
}): React.JSX.Element {
  const toast = useToast();
  const updateMutation = useUpdateProject();
  const persisted = website ?? '';
  const [value, setValue] = useState(persisted);

  // Re-seed the draft when the persisted website changes (project switch or a
  // successful save's cache invalidation) so the input mirrors the source of truth.
  useEffect(() => {
    setValue(persisted);
  }, [persisted]);

  const dirty = value.trim() !== persisted.trim();

  function save(): void {
    if (!dirty || updateMutation.isPending) return;
    const next = value.trim();
    updateMutation.mutate(
      { projectId, updates: { client_website: next === '' ? null : next } },
      {
        onSuccess: () => toast.success('Website saved'),
        onError: () => toast.error('Could not save website'),
      },
    );
  }

  return (
    <label className="mt-3 block text-xs font-medium text-text-muted">
      Website
      <div className="mt-1 flex items-center gap-2">
        <input
          type="url"
          inputMode="url"
          data-testid="prekickoff-company-website"
          value={value}
          placeholder="https://example.com"
          onChange={(e) => setValue(e.target.value)}
          className="w-full rounded border border-border px-3 py-1.5 text-sm text-text focus:border-primary focus:outline-none"
        />
        <button
          type="button"
          data-testid="prekickoff-company-website-save"
          onClick={save}
          disabled={!dirty || updateMutation.isPending}
          title="Save the company website"
          className="shrink-0 rounded border border-border px-3 py-1.5 text-sm font-medium text-primary hover:bg-surface-muted disabled:opacity-60"
        >
          {updateMutation.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </label>
  );
}

export default PreKickoffPanel;
